import { btnDemo, tlink } from '../components/cta';
import { architecture, escalation, execVisual, smartRouting } from '../components/diagrams';
import { workflowPanel } from '../components/hero-panel';
import { icon } from '../components/icons';
import { inceptionBadge } from '../components/nvidia-inception';
import { eyebrow, mesh } from '../components/primitives';
import {
  INSIGHT_NOTE,
  businessOutcome,
  ctaBand,
  foundationTiles,
  howWeWork,
  impactCards,
  insightCard,
  northAmerica,
  sigBand,
} from '../components/sections';
import { INCEPTION_MEMBER_LABEL } from '../content/nvidia-inception';
import { routeHref } from '../lib/links';
import { BIGGER_STORY, FOUNDER_QUOTE, ROUTING_HEAD } from './shared-content';

/* ============================ HOME ============================ */
const CAPS = [
  ['intake', 'inbox', 'Intelligent Intake', 'Turn incoming information into structured work.'],
  [
    'document-intelligence',
    'file',
    'Document Intelligence',
    'Turn unstructured documents into structured workflow data.',
  ],
  [
    'settlement',
    'swap',
    'Settlement Intelligence',
    'Bring settlement activity into the same operating environment as the rest of the appeal.',
  ],
  [
    'client-portal',
    'monitor',
    'Client Portal &amp; Portfolio Experience',
    'Give clients controlled visibility into the work that matters to them.',
  ],
  [
    'billing',
    'receipt',
    'Billing &amp; Financials',
    'Case financials, invoicing, fee tracking and payment status in one place.',
  ],
  [
    'reporting',
    'chart',
    'Reporting &amp; Portfolio Intelligence',
    'Turn operational activity into portfolio-level visibility.',
  ],
] as const;

const TRUST_POINTS = [
  ['key', 'Single Sign-On'],
  ['users', 'Role-based access control'],
  ['database', 'Customer-owned data'],
] as const;

export function pageHome(): string {
  return `
  <section class="hero" aria-labelledby="h1">
    ${mesh()}
    <div class="wrap hero-grid">
      <div class="rv">
        ${eyebrow('Enterprise AI orchestration for property tax')}
        <h1 id="h1">AI + Expertise. <span class="grad">Built around how you work.</span></h1>
        <p class="lede">Property tax operations are complex. ReTax360 brings clients, communication, data and documents into one foundation, then intelligently coordinates work between AI, business rules, systems and the people responsible for making the decisions that matter.</p>
        <div class="ctas">${btnDemo()}<a class="btn btn-ghost" href="${routeHref('/platform')}">Explore the Platform</a></div>
        <div class="meta"><span><span class="dot"></span>Enterprise AI orchestration &amp; workflow</span><span><span class="dot"></span>Human-in-the-loop</span><span><span class="dot"></span>United States &amp; Canada</span></div>
      </div>
      <div class="stage rv" style="--d:120ms">
        ${workflowPanel()}
        <div class="float f1" aria-hidden="true"><span class="ic">${icon('clock')}</span><span><b>Next deadline · {{DL_S}}</b><small>Cook County, IL · {{DUE}}</small></span></div>
        <div class="float f2" aria-hidden="true" data-review-float><span class="ic">${icon('userCheck')}</span><span><b>Professional review</b><small data-review-text>Assigned to Morgan Lee · human checkpoint</small></span></div>
      </div>
    </div>
  </section>

  <section class="trust-strip" aria-label="Security and program membership">
    <div class="wrap">
      <ul class="trust-points">${TRUST_POINTS.map(([i, t]) => `<li>${icon(i)}<span>${t}</span></li>`).join('')}</ul>
      <p class="trust-badge">${inceptionBadge('inception-badge')}<span>${INCEPTION_MEMBER_LABEL}</span></p>
    </div>
  </section>

  <section class="s" id="lifecycle" aria-labelledby="life-h">
    <div class="wrap">
      <div class="head rv">${eyebrow('The platform in 10 seconds')}<h2 id="life-h">One intelligence layer across the entire property tax lifecycle.</h2><p>ReTax360 is an enterprise AI orchestration and workflow platform purpose-built for property tax operations. It routes work between AI, business rules, systems and people, instead of forcing every task through one AI model or one rigid workflow.</p></div>
      ${execVisual()}
    </div>
  </section>

  ${sigBand('Systems of record remember what happened. ReTax360 is built to coordinate what happens next.', { dark: true, grad: 'coordinate what happens next.' })}

  <section class="s" id="thesis" aria-labelledby="thesis-h">
    <div class="wrap thesis">
      <div class="rv">
        ${eyebrow('Our thesis')}
        <h2 id="thesis-h" style="margin:16px 0 20px">The goal isn't to use more AI. The goal is to use the right intelligence at the right moment.</h2>
        <p class="lede">Not every task needs AI. Not every AI task needs the same model. Not every workflow should operate identically. And not every decision should be automated. ReTax360's role is to intelligently coordinate those resources inside one governed workflow.</p>
        <p class="apx">AI prepares. Rules govern. Experts decide.</p>
      </div>
      <div>
        ${INSIGHT_NOTE}
        ${insightCard("This is the first time I've heard anyone put AI, expertise and workflow together.", 'Property tax professional, enterprise evaluation')}
      </div>
    </div>
  </section>

  <section class="s alt" id="foundation" aria-labelledby="foundation-h">
    <div class="wrap">
      <div class="head-row">
        <div class="head rv">${eyebrow('One foundation')}<h2 id="foundation-h">Every firm works a little differently. The foundation doesn't.</h2><p>Clients. Communication. Data. Documents. Above that foundation sits an intelligence and orchestration layer, and above that, the workflow configured around your organization.</p></div>
        <div class="rv">${tlink('Explore the architecture', routeHref('/platform#architecture'))}</div>
      </div>
      <div class="arch-home">${architecture({ compact: true })}</div>
      <div style="margin-top:28px">${foundationTiles()}</div>
    </div>
  </section>

  <section class="s dark" id="routing" aria-labelledby="routing-h">
    <div class="wrap">
      ${ROUTING_HEAD('routing-h')}
      ${smartRouting()}
      ${escalation({ compact: true })}
    </div>
  </section>

  <section class="s" id="capabilities" aria-labelledby="cap-h">
    <div class="wrap">
      <div class="head rv">${eyebrow('Capabilities')}<h2 id="cap-h">Configured applications on one governed foundation.</h2></div>
      <ul class="grid3" data-stagger>${CAPS.map(([a, i, h, p]) => `<li class="card lift mod rv"><span class="ic-tile">${icon(i)}</span><h3>${h}</h3><p>${p}</p><div class="kw">${tlink(`Learn more<span class="sr-only"> about ${h}</span>`, routeHref('/platform#' + a))}</div></li>`).join('')}</ul>
    </div>
  </section>

  ${sigBand('AI prepares. Rules govern. Experts decide.')}
  ${howWeWork({ full: false })}

  <section class="s tight-top" id="impact" aria-labelledby="impact-h">
    <div class="wrap">${impactCards()}${businessOutcome()}</div>
  </section>

  ${sigBand('Expertise deserves better infrastructure.', { dark: true, grad: 'better infrastructure.' })}
  ${northAmerica()}

  <section class="s alt" id="governance" aria-labelledby="gov-t-h">
    <div class="wrap">
      <div class="head-row">
        <div class="head rv">${eyebrow('Enterprise governance')}<h2 id="gov-t-h">Enterprise-grade by design. AI you can explain.</h2></div>
        <div class="rv">${tlink('Visit Security &amp; Trust', routeHref('/security'))}</div>
      </div>
      <ul class="grid4" data-stagger>
        <li class="card rv"><span class="ic-tile">${icon('key')}</span><h3>Single Sign-On</h3><span class="st avail">Supported</span></li>
        <li class="card rv"><span class="ic-tile">${icon('users')}</span><h3>Role-Based Access Control</h3><span class="st avail">Available</span></li>
        <li class="card rv"><span class="ic-tile">${icon('userCheck')}</span><h3>Human approval gates</h3><p>Critical AI-assisted outputs can require designated human review.</p></li>
        <li class="card rv"><span class="ic-tile">${icon('listChecks')}</span><h3>Auditability</h3><p>Workflow actions can be logged to show how work moved through the system.</p></li>
      </ul>
    </div>
  </section>

  <section class="s" aria-label="Founder">
    <div class="wrap">${FOUNDER_QUOTE}</div>
  </section>

  <section class="s tight-top" aria-labelledby="story-h">
    <div class="wrap">${BIGGER_STORY('story-h')}</div>
  </section>

  ${ctaBand()}`;
}
